/**
 * The Build-tab save path for a spec-bearing ("LLM App") workflow.
 *
 * The spec is the source of truth: a write carrying spec_json is recompiled into the
 * graph server-side (models/AppWorkflow.model.js's _applySpec), so without this sync
 * every model/connection/prompt/output-structure edit made on a compiled workflow's
 * canvas node would be silently discarded by the very Save that confirmed it.
 *
 * So the canvas intelligence node's editable config is synced back INTO the spec before
 * the write — one direction, node → spec, over the exact fields the inspector can author.
 * Everything the spec owns that the canvas cannot express (input, client_tools) is left
 * alone, and a spec the canvas does not mirror (wrong node type deleted, unparsable)
 * passes through untouched — the server's own validation then rejects what it must.
 *
 * Pure, so the sync round-trips identically in the dirty-tracking baseline and in tests.
 */

/** Canvas node config key → spec key, per mode. `prompt` maps to `user_prompt` everywhere. */
const FIELD_MAP_LLM = [
  ['provider', 'provider'],
  ['model', 'model'],
  ['connection_id', 'connection_id'],
  ['system_prompt', 'system_prompt'],
];
const FIELD_MAP_AGENT = [
  ['agent_key', 'agent_key'],
  ['max_tool_loops', 'max_tool_loops'],
];

function syncRouting(next, data, map) {
  for (const [nodeKey, specKey] of map) {
    if (!(nodeKey in data)) continue;
    const v = data[nodeKey];
    if (v === null || v === undefined || v === '') delete next[specKey];
    else next[specKey] = v;
  }
}

function syncOutput(next, data) {
  if (!('output_schema' in data)) return;
  const raw = data.output_schema;
  if (raw === null || raw === undefined || raw === '') {
    delete next.output;
    return;
  }
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      if (parsed.length) next.output = parsed;
      else delete next.output;
    }
    // A map-shaped or corrupt value stays in the node's own config — the graph validator
    // rejects it with a node-scoped message, so the save fails loud either way.
  } catch {
    // Same stance: leave the spec untouched; save-time graph validation reports the node.
  }
}

/** The intelligence node a compiled graph carries, per mode — the only node the sync reads. */
function intelligenceNode(spec, graph) {
  const nodeType = spec.mode === 'agent' ? 'agent_turn' : 'llm';
  const nodes = graph && Array.isArray(graph.nodes) ? graph.nodes : [];
  const node = nodes.find((n) => n && n.type === nodeType);
  return node && node.data && typeof node.data === 'object' ? node : null;
}

/**
 * Returns the spec_json string with the canvas intelligence node's config applied.
 * Anything unparsable in, unparsable out — no silent normalization here.
 */
export function syncSpecWithGraph(specJson, graph) {
  let spec;
  try { spec = typeof specJson === 'string' ? JSON.parse(specJson) : specJson; } catch { return specJson; }
  if (!spec || typeof spec !== 'object' || spec.kind !== 'llm_app') return specJson;
  const node = intelligenceNode(spec, graph);
  if (!node) return specJson;
  const data = node.data;

  const next = { ...spec };
  syncRouting(next, data, spec.mode === 'agent' ? FIELD_MAP_AGENT : FIELD_MAP_LLM);
  if (spec.mode !== 'agent') {
    // The run resolves the credential wholesale from the connection, provider included —
    // a spec provider the node doesn't carry would be a lie about what runs.
    if (next.connection_id != null && typeof next.provider !== 'string') delete next.provider;
  }
  if (typeof data.prompt === 'string' && data.prompt.trim()) next.user_prompt = data.prompt;
  syncOutput(next, data);
  return JSON.stringify(next);
}
